import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import CartDrawer from "@/components/CartDrawer";
import FloatingCart from "@/components/FloatingCart";
import { api, trackWaClick } from "@/lib/api";
import { rupiah } from "@/lib/format";
import { useCart } from "@/context/CartContext";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { motion } from "framer-motion";
import { Drum, Piano, Guitar, Mic2, Speaker, ArrowLeft, BadgePercent, Music as MusicIcon, Loader2, CalendarCheck, MessageCircle } from "lucide-react";
import { toast } from "sonner";

const INSTRUCTORS = [
  "Abet Nigo, S.M. (Head Instructor)",
  "Kak Rani (Vokal & Piano)",
  "Kak Bima (Drum & Gitar/Bass)",
  "Bebas — rekomendasi admin",
];

const DAYS = ["Senin", "Selasa", "Rabu", "Kamis", "Jumat", "Sabtu"];
const TIME_SLOTS = ["09.00–10.00", "13.00–14.00", "16.00–17.00", "19.00–20.00"];

const PACKAGE_LABELS = { drum: "Les Drum", piano: "Les Piano", gitar: "Les Gitar/Bass", vokal: "Les Vokal" };

const POSTER = "https://customer-assets-7cd3h4nn.emergentagent.net/job_fasmart-online/artifacts/qluwfx8p_IMG-20260730-WA0000.jpg";
const WA_ADMIN = "6281377114000"; // Nomor WhatsApp admin FAS MUSIC

const PACKAGES = [
  { key: "Drum", icon: Drum, name: "Les Drum", price: 450000, per: "/bulan", img: "https://images.unsplash.com/photo-1524230659092-07f99a75c013?auto=format&fit=crop&w=700&q=80" },
  { key: "Piano", icon: Piano, name: "Les Piano", price: 450000, per: "/bulan", img: "https://images.unsplash.com/photo-1577877777751-3f1ec20a0715?auto=format&fit=crop&w=700&q=80" },
  { key: "Gitar", icon: Guitar, name: "Les Gitar / Bass", price: 450000, per: "/bulan", img: "https://images.pexels.com/photos/7520968/pexels-photo-7520968.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" },
  { key: "Vokal", icon: Mic2, name: "Les Vokal", price: 450000, per: "/bulan", img: "https://images.pexels.com/photos/7521301/pexels-photo-7521301.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" },
  { key: "Studio", icon: Speaker, name: "Sewa Studio Band", price: 100000, per: "/jam", img: "https://images.pexels.com/photos/7091308/pexels-photo-7091308.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" },
];

export default function Music() {
  const [products, setProducts] = useState([]);
  const { add } = useCart();
  const [form, setForm] = useState({ student_name: "", phone: "", package: "drum", day: "Senin", time_slot: TIME_SLOTS[0], instructor: INSTRUCTORS[3], notes: "" });
  const [loading, setLoading] = useState(false);
  const [takenSlots, setTakenSlots] = useState([]);
  const loadSlots = () => api.get("/music/slots").then((r) => setTakenSlots(r.data)).catch(() => {});

  useEffect(() => { loadSlots(); }, []);

  const submitRegistration = async () => {
    if (!form.student_name.trim() || !form.phone.trim()) {
      return toast.error("Lengkapi nama murid dan nomor HP");
    }
    setLoading(true);
    try {
      await api.post("/music/register", form);
      const msg = [
        "Halo Admin FAS MUSIC, saya mau daftar les:",
        `• Nama murid: ${form.student_name}`,
        `• Paket: ${PACKAGE_LABELS[form.package]}`,
        `• Jadwal: ${form.day}, ${form.time_slot}`,
        `• Instruktur: ${form.instructor}`,
        `• HP/WA: ${form.phone}`,
        form.notes ? `• Catatan: ${form.notes}` : null,
      ].filter(Boolean).join("\n");
      window.open(`https://wa.me/${WA_ADMIN}?text=${encodeURIComponent(msg)}`, "_blank");
      toast.success("Pendaftaran terkirim!", { description: "Lanjut konfirmasi ke admin di WhatsApp yang baru terbuka." });
      setForm({ student_name: "", phone: "", package: "drum", day: "Senin", time_slot: TIME_SLOTS[0], instructor: INSTRUCTORS[3], notes: "" });
      loadSlots();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal mengirim pendaftaran");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    api.get("/products?category=FAS%20MUSIC").then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  const daftar = (pkg) => {
    const prod = products.find((p) => p.name.includes(pkg.key));
    if (!prod) return toast.error("Paket belum tersedia, coba lagi sebentar");
    add(prod, 1);
    toast.success(`${pkg.name} masuk keranjang`, { description: "Diskon member 10% otomatis terpasang" });
  };

  return (
    <div className="min-h-screen bg-[#0A1B3D] text-white">
      <Navbar />
      <CartDrawer />
      <FloatingCart />

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 pt-8 pb-6">
        <Link to="/" data-testid="back-to-home-btn" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-amber-400 text-[#0A1B3D] text-sm font-bold shadow-md hover:bg-amber-300 hover:-translate-y-0.5 transition">
          <ArrowLeft className="w-4 h-4" /> Kembali Belanja
        </Link>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-8 items-center rounded-3xl border border-amber-400/20 bg-gradient-to-br from-[#0D2450] to-[#0A1B3D] p-6 md:p-10"
        >
          <div>
            <span className="tag-pill bg-amber-400 text-[#0A1B3D]">
              <MusicIcon className="w-3 h-3" /> FAS MUSIC
            </span>
            <h1 className="mt-5 font-display font-black text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              Semua Bisa <span className="text-amber-400">Bermain Alat Musik</span>
            </h1>
            <p className="mt-5 text-blue-100/85 text-sm md:text-base leading-relaxed max-w-md">
              Setiap anak bisa belajar, bertumbuh, dan berkarya melalui musik. Les oleh instruktur berpengalaman — Abet Nigo, S.M. & tim FAS MUSIC.
            </p>
            <div className="mt-6 rounded-2xl bg-amber-400/10 border border-amber-400/30 px-5 py-4 inline-flex items-center gap-3">
              <BadgePercent className="w-6 h-6 text-amber-400 shrink-0" />
              <div>
                <div className="font-bold text-amber-300">Diskon 10% Semua Paket</div>
                <div className="text-xs text-blue-100/80">Khusus member FAS Group Corp.</div>
              </div>
            </div>
            <div className="mt-6">
              <a href="#paket-les" data-testid="music-cta" className="inline-block px-6 py-3 rounded-full bg-amber-400 text-[#0A1B3D] font-bold hover:bg-amber-300 transition shadow-lg shadow-amber-400/30">
                Lihat Paket Les ↓
              </a>
            </div>
          </div>
          <div className="flex justify-center">
            <img
              src={POSTER}
              alt="Poster FAS MUSIC — Semua Bisa Bermain Alat Musik"
              data-testid="music-poster"
              className="w-full max-w-sm rounded-3xl border-4 border-amber-400/40 shadow-2xl shadow-amber-400/10 rotate-1 hover:rotate-0 transition-transform duration-500"
            />
          </div>
        </motion.div>
      </section>

      {/* Paket les */}
      <section id="paket-les" className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        <h2 className="font-display font-black text-3xl md:text-4xl tracking-tight">Paket Les & Studio</h2>
        <p className="text-blue-100/70 mt-1">Semua harga sudah termasuk diskon member 10% saat checkout.</p>
        <div data-testid="music-schedule-info" className="mt-3 inline-flex items-center gap-2 rounded-full bg-blue-900/50 border border-blue-800 px-4 py-2 text-sm font-semibold text-blue-100">
          <CalendarCheck className="w-4 h-4 text-amber-400" />
          Paket les: <b>4x pertemuan per bulan</b> · 1x pertemuan <b>45 menit</b>
        </div>
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {PACKAGES.map((pkg, i) => (
            <motion.div
              key={pkg.key}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: i * 0.07 }}
              data-testid={`music-package-${pkg.key.toLowerCase()}`}
              className="rounded-3xl overflow-hidden bg-[#0D2450] border border-blue-900/60 card-tilt"
            >
              <div className="relative h-44">
                <img src={pkg.img} alt={pkg.name} className="w-full h-full object-cover" />
                <span className="absolute top-2 left-2 tag-pill bg-amber-400 text-[#0A1B3D] text-[10px]">−10% Member</span>
              </div>
              <div className="p-5">
                <div className="flex items-center gap-2 text-amber-400">
                  <pkg.icon className="w-4 h-4" />
                  <span className="text-[10px] font-mono uppercase tracking-[0.2em]">FAS MUSIC</span>
                </div>
                <h3 className="mt-2 font-display font-bold text-xl">{pkg.name}</h3>
                <div className="mt-2 flex items-baseline gap-2">
                  <span className="font-display font-black text-2xl text-amber-400">{rupiah(Math.round(pkg.price * 0.9))}</span>
                  <span className="text-sm text-blue-200/60 line-through">{rupiah(pkg.price)}</span>
                  <span className="text-xs text-blue-100/70">{pkg.per}</span>
                </div>
                <button
                  data-testid={`music-daftar-${pkg.key.toLowerCase()}`}
                  onClick={() => daftar(pkg)}
                  className="mt-4 w-full py-3 rounded-xl bg-amber-400 text-[#0A1B3D] font-bold hover:bg-amber-300 transition"
                >
                  Daftar Sekarang
                </button>
              </div>
            </motion.div>
          ))}
        </div>
        <p className="mt-6 text-center text-xs text-blue-100/60">
          Jadwal les dikonfirmasi admin setelah pembayaran via warung FASMART · Studio tersedia setiap hari 09.00–21.00
        </p>
      </section>

      {/* Form pendaftaran les */}
      <section id="daftar-les" className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        <div className="rounded-3xl bg-gradient-to-br from-[#0D2450] to-[#081231] border border-amber-400/20 p-8 md:p-10">
          <h2 className="font-display font-black text-3xl md:text-4xl tracking-tight">Form Pendaftaran Les</h2>
          <p className="text-blue-100/80 mt-2 max-w-xl">Isi data murid & pilih jadwal — admin FAS MUSIC konfirmasi via WhatsApp. Pembayaran paket les dilakukan lewat keranjang/checkout.</p>
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-3xl">
            <div>
              <Label className="text-blue-100">Nama Murid</Label>
              <Input data-testid="music-form-name" value={form.student_name} onChange={(e) => setForm({ ...form, student_name: e.target.value })} className="mt-1 bg-white text-stone-900" placeholder="Nama anak/murid" />
            </div>
            <div>
              <Label className="text-blue-100">Nomor HP / WA Orang Tua</Label>
              <Input data-testid="music-form-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1 bg-white text-stone-900" placeholder="0812xxxxxxxx" />
            </div>
            <div>
              <Label className="text-blue-100">Paket Les</Label>
              <Select value={form.package} onValueChange={(v) => setForm({ ...form, package: v })}>
                <SelectTrigger data-testid="music-form-package" className="mt-1 bg-white text-stone-900"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(PACKAGE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v} — Rp 450rb/bln</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-blue-100">Instruktur</Label>
              <Select value={form.instructor} onValueChange={(v) => setForm({ ...form, instructor: v })}>
                <SelectTrigger data-testid="music-form-instructor" className="mt-1 bg-white text-stone-900"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {INSTRUCTORS.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-blue-100">Hari Les</Label>
              <Select value={form.day} onValueChange={(v) => setForm({ ...form, day: v })}>
                <SelectTrigger data-testid="music-form-day" className="mt-1 bg-white text-stone-900"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {DAYS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-blue-100">Sesi Jam</Label>
              <Select value={form.time_slot} onValueChange={(v) => setForm({ ...form, time_slot: v })}>
                <SelectTrigger data-testid="music-form-time" className="mt-1 bg-white text-stone-900"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TIME_SLOTS.map((t) => {
                    const taken = takenSlots.some((s) => s.day === form.day && s.time_slot === t);
                    return (
                      <SelectItem key={t} value={t} disabled={taken} data-testid={`music-slot-${t}`}>
                        {t}{taken ? " · Terisi" : ""}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2">
              <Label className="text-blue-100">Catatan (opsional)</Label>
              <Textarea data-testid="music-form-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1 bg-white text-stone-900" rows={2} placeholder="Mis. murid pemula, pernah les sebelumnya, dll" />
            </div>
          </div>
          <Button
            data-testid="music-form-submit-btn"
            onClick={submitRegistration}
            disabled={loading}
            className="mt-6 rounded-full bg-amber-400 text-[#0A1B3D] hover:bg-amber-300 font-bold px-8 py-6 text-base"
          >
            {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Mengirim...</> : <><CalendarCheck className="w-4 h-4 mr-2" /> Kirim Pendaftaran Les</>}
          </Button>
        </div>
      </section>

      <footer className="relative border-t border-blue-900/60 py-6 text-center text-xs text-blue-100/50 font-mono uppercase tracking-widest">
        <a data-testid="footer-wa-link" onClick={() => trackWaClick("music")} href="https://wa.me/6281377114000?text=Halo%20Admin%20FAS%20Group%2C%20saya%20mau%20tanya%20FAS%20MUSIC..." target="_blank" rel="noopener noreferrer" className="mb-3 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-600 text-white text-sm font-bold normal-case tracking-normal hover:bg-emerald-500 transition">
          <MessageCircle className="w-4 h-4" /> Admin FAS Group Corp.
        </a>
        <div>FAS MUSIC by FAS Group Corp. · Semua Bisa Bermain Alat Musik</div>
        <img src="https://customer-assets-7cd3h4nn.emergentagent.net/job_fasmart-online/artifacts/kpyu0nvc_ChatGPT%20Image%2026%20Agu%202026%2C%2012.19.43.png" alt="FAS Group Corp" data-testid="footer-fasgroup-logo" className="mx-auto mt-4 h-14 w-auto object-contain rounded-xl bg-white p-1.5 md:absolute md:right-6 md:bottom-4 md:m-0 md:h-16" />
      </footer>
    </div>
  );
}
